// @flow
import React, { useContext, useState } from 'react';
import { isEmpty } from 'lodash';
import classnames from 'classnames';

import ratioUtils from 'Components/utils/ratios/ratios.utils.js';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import Image from 'Components/frameworks/image/Image.jsx';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { themes } from './ThumbnailMenu.themes';

export type ThumbnailMenuProps = {
  className?: string,
  color?: Colors,
  label: string,
  link?: string,
  menus?: Array<{
    label: string,
    trackingData?: TrackingData,
    url: string,
  }>,
  theme?: string,
  thumbnail: {|
    imageUrl: string,
    ratioKey?: MediaRatios,
  |},
  trackingData?: TrackingData,
};

export function ThumbnailMenu(props: ThumbnailMenuProps) {
  const {
    className,
    color,
    label,
    link,
    menus = [],
    theme = 'default',
    thumbnail = {},
    trackingData,
  } = props;

  const hasMenus = !isEmpty(menus);

  const { isBrowserGreaterThan } = useContext(ResponsiveContext);

  const [isExpand, setExpand] = useState(false);

  const {
    thBody,
    thBodyStyle,
    thLabel,
    thLabelStyle,
    thMenus,
    thMenu,
    thRoot,
    thThumbnail,
  } = themes[theme]({
    color,
    hasMenus,
    isBrowserGreaterThan,
    isExpand,
  });

  const thumbnailImage = (
    <Image className={thThumbnail} {...thumbnail} ratioKey={thumbnail.ratioKey || ratioUtils.XL} />
  );

  const heading = (
    <h2 className={thLabel} style={thLabelStyle}>
      {label}
    </h2>
  );

  return (
    <article
      className={classnames(className, thRoot, 'image-responsive-cell')}
      onMouseEnter={isBrowserGreaterThan.S && hasMenus ? () => setExpand(true) : null}
      onMouseLeave={isBrowserGreaterThan.S && hasMenus ? () => setExpand(false) : null}
    >
      {link ? (
        <TrackClick
          className="block overflow-hidden"
          as={DynamicLink}
          trackingData={trackingData}
          url={link}
        >
          {thumbnailImage}
        </TrackClick>
      ) : (
        thumbnailImage
      )}
      <div className={thBody} style={thBodyStyle}>
        {link ? (
          <TrackClick className="block" as={DynamicLink} trackingData={trackingData} url={link}>
            {heading}
          </TrackClick>
        ) : (
          heading
        )}
        {hasMenus && (
          <ul className={thMenus}>
            {menus.map(menu => (
              <li key={menu.label}>
                <TrackClick
                  as={DynamicLink}
                  className={thMenu}
                  trackingData={menu.trackingData}
                  url={menu.url}
                  onBlur={isBrowserGreaterThan.S && hasMenus ? () => setExpand(false) : null}
                  onFocus={isBrowserGreaterThan.S && hasMenus ? () => setExpand(true) : null}
                >
                  {menu.label}
                </TrackClick>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}
